/**
 * OutcomeDistributionChart — stacked bar chart showing outcome code distribution per cell.
 *
 * Lot 7 — §4.5 outcome distribution chart.
 *
 * Reads `stats.cNever`, `stats.cDrop`, `stats.cKept`, `stats.cReco`, `stats.cX`
 * from each cell's `ConversationWindowSlice.stats` in the ROLLUP row.
 *
 * Colours (D27 — each colour carries a `direction` from the registry):
 *   cReco  → success (green)
 *   cKept  → info (blue)
 *   cDrop  → warning (amber)
 *   cNever → error (red)
 *   cX     → text.disabled (grey)
 *
 * This is a pure SVG + MUI implementation (no recharts dep) to keep the bundle small.
 */

import { Box, Chip, Tooltip, Typography, useTheme } from '@mui/material';
import type { ConversationCounters } from '@traaker/shared';

interface CellOutcome {
  cellKey: string;
  stats: ConversationCounters | undefined;
}

interface OutcomeDistributionChartProps {
  cells: CellOutcome[];
  /** Show legend inline (default: true). */
  showLegend?: boolean;
}

function cellLabel(ck: string): string {
  const [engine, language, zone] = ck.split('#');
  const engineLabel: Record<string, string> = {
    openai: 'ChatGPT',
    perplexity: 'Perplexity',
    gemini: 'Gemini',
    mistral: 'Mistral',
  };
  return `${engineLabel[engine] ?? engine} · ${language.toUpperCase()} · ${zone}`;
}

/** Outcome segment — a fraction of the bar width. */
interface Segment {
  label: string;
  count: number;
  colorKey: 'success' | 'info' | 'warning' | 'error' | 'disabled';
  tooltipText: string;
}

function buildSegments(stats: ConversationCounters): Segment[] {
  if (!stats) return [];
  const cDrop = Array.isArray(stats.cDrop) ? stats.cDrop.reduce((s: number, v: number) => s + v, 0) : 0;
  const total = stats.n + (stats.cX ?? 0);
  if (total === 0) return [];
  const cReco = stats.cReco ?? 0;
  // R ⊆ M: the M segment is "cited to end but NOT recommended" to avoid double-counting
  const cKeptNotReco = Math.max(0, (stats.cKept ?? 0) - cReco);
  return [
    {
      label: 'R',
      count: cReco,
      colorKey: 'success' as const,
      tooltipText: `Recommandé (R): ${cReco}`,
    },
    {
      label: 'M',
      count: cKeptNotReco,
      colorKey: 'info' as const,
      tooltipText: `Cité jusqu'à la fin mais non recommandé (M−R): ${cKeptNotReco}`,
    },
    {
      label: 'drop',
      count: cDrop,
      colorKey: 'warning' as const,
      tooltipText: `Décroché en chemin: ${cDrop}`,
    },
    {
      label: '1',
      count: stats.cNever ?? 0,
      colorKey: 'error' as const,
      tooltipText: `Jamais cité (1): ${stats.cNever ?? 0}`,
    },
    {
      label: 'x',
      count: stats.cX ?? 0,
      colorKey: 'disabled' as const,
      tooltipText: `Échec moteur (x): ${stats.cX ?? 0}`,
    },
  ].filter((s) => s.count > 0);
}

const BAR_WIDTH = 200;
const BAR_HEIGHT = 18;
const BAR_RADIUS = 4;

function StackedBar({ segments, total }: { segments: Segment[]; total: number }) {
  const theme = useTheme();

  const colorMap: Record<Segment['colorKey'], string> = {
    success: theme.palette.success.main,
    info: theme.palette.info.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    disabled: theme.palette.text.disabled,
  };

  if (total === 0) {
    return (
      <Box
        component="svg"
        width={BAR_WIDTH}
        height={BAR_HEIGHT}
        sx={{ display: 'block' }}
      >
        <rect
          x={0} y={0}
          width={BAR_WIDTH} height={BAR_HEIGHT}
          rx={BAR_RADIUS}
          fill={theme.palette.action.hover}
        />
        <text
          x={BAR_WIDTH / 2} y={BAR_HEIGHT / 2 + 4}
          textAnchor="middle"
          fontSize={10}
          fill={theme.palette.text.disabled}
        >
          non mesuré
        </text>
      </Box>
    );
  }

  let xOffset = 0;
  return (
    <Box component="svg" width={BAR_WIDTH} height={BAR_HEIGHT} sx={{ display: 'block' }}>
      {/* Background */}
      <rect x={0} y={0} width={BAR_WIDTH} height={BAR_HEIGHT} rx={BAR_RADIUS} fill={theme.palette.action.hover} />
      {/* Segments */}
      {segments.map((seg, i) => {
        const segWidth = (seg.count / total) * BAR_WIDTH;
        const x = xOffset;
        xOffset += segWidth;
        const isFirst = i === 0;
        const isLast = i === segments.length - 1;
        return (
          <Tooltip key={seg.label} title={seg.tooltipText}>
            <rect
              x={x}
              y={0}
              width={Math.max(segWidth, 0)}
              height={BAR_HEIGHT}
              rx={isFirst && isLast ? BAR_RADIUS : isFirst ? BAR_RADIUS : isLast ? BAR_RADIUS : 0}
              fill={colorMap[seg.colorKey]}
              opacity={0.85}
              style={{ cursor: 'default' }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
}

export function OutcomeDistributionChart({
  cells,
  showLegend = true,
}: OutcomeDistributionChartProps) {
  const theme = useTheme();

  if (cells.length === 0) return null;

  return (
    <Box>
      {showLegend && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          {[
            { label: 'Recommandé', color: theme.palette.success.main },
            { label: 'Cité jusqu\'à la fin', color: theme.palette.info.main },
            { label: 'Décroché', color: theme.palette.warning.main },
            { label: 'Jamais cité', color: theme.palette.error.main },
            { label: 'Échec moteur', color: theme.palette.text.disabled },
          ].map(({ label, color }) => (
            <Chip
              key={label}
              label={label}
              size="small"
              sx={{ height: 20, fontSize: '0.7rem', bgcolor: color, color: '#fff' }}
            />
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {cells.map(({ cellKey, stats }) => {
          const segments = stats ? buildSegments(stats) : [];
          const total = (stats?.n ?? 0) + (stats?.cX ?? 0);
          return (
            <Box key={cellKey} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ minWidth: 160, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {cellLabel(cellKey)}
              </Typography>
              <StackedBar segments={segments} total={total} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ minWidth: 40, textAlign: 'right' }}
              >
                {total > 0 ? `${total} conv.` : ''}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
